/**
 * La marca (Nike, Apple…) y las etiquetas («verano», «oferta») de una oferta:
 * se eligen entre las que ya usan las demás o se crean escribiéndolas. El mismo
 * campo que los autores y las categorías del blog —`PickerField`—, así que
 * también se puede quitar una de todas las ofertas desde la lista.
 */
import { PickerField, type KnownOption } from './BlogPickerField'
import { removeOfferBrand, removeOfferTag, type Offer } from '../../../lib/api'
import { useOfferLabels } from '../../../lib/offerLabels'

/** Las de la lista del proyecto y, sin repetir, las que traiga la pantalla. */
function merged(shared: KnownOption[], extra: KnownOption[]): KnownOption[] {
  const seen = new Set(shared.map((option) => option.name.toLocaleLowerCase()))
  return [...shared, ...extra.filter((option) => !seen.has(option.name.toLocaleLowerCase()))]
}

/** Las marcas del proyecto, con cuántas ofertas las llevan; sin distinguir mayúsculas. */
export function knownBrands(offers: Offer[]): KnownOption[] {
  const counts = new Map<string, KnownOption>()
  for (const offer of offers) {
    const name = offer.brand?.trim()
    if (!name) continue
    const key = name.toLocaleLowerCase()
    const current = counts.get(key)
    counts.set(key, { name: current?.name ?? name, count: (current?.count ?? 0) + 1 })
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

/** Las etiquetas del proyecto, con cuántas ofertas las llevan; sin distinguir mayúsculas. */
export function knownTags(offers: Offer[]): KnownOption[] {
  const counts = new Map<string, KnownOption>()
  for (const offer of offers) {
    for (const raw of offer.tags ?? []) {
      const name = raw.trim()
      if (!name) continue
      const key = name.toLocaleLowerCase()
      const current = counts.get(key)
      counts.set(key, { name: current?.name ?? name, count: (current?.count ?? 0) + 1 })
    }
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

/** Quitar una etiqueta de las ofertas que ya tiene en memoria la pantalla. */
export function withoutTag(offers: Offer[], name: string): Offer[] {
  const lower = name.toLocaleLowerCase()
  return offers.map((offer) => (offer.tags?.some((tag) => tag.toLocaleLowerCase() === lower)
    ? { ...offer, tags: offer.tags.filter((tag) => tag.toLocaleLowerCase() !== lower) }
    : offer))
}

/** Quitar una marca de las ofertas que ya tiene en memoria la pantalla. */
export function withoutBrand(offers: Offer[], name: string): Offer[] {
  const lower = name.toLocaleLowerCase()
  return offers.map((offer) => (offer.brand?.trim().toLocaleLowerCase() === lower ? { ...offer, brand: '' } : offer))
}

const BRAND_TEXTS = {
  label: 'offers.brand.label',
  placeholder: 'offers.brand.placeholder',
  create: 'offers.brand.create',
  empty: 'offers.brand.empty',
  allChosen: 'offers.brand.allChosen',
  limit: 'offers.brand.limit',
  remove: 'offers.brand.remove',
  deleteAria: 'offers.brand.deleteAria',
  deleteTitle: 'offers.brand.deleteTitle',
  deleteBody: 'offers.brand.deleteBody',
  deleteBodyOne: 'offers.brand.deleteBodyOne',
  deleteConfirm: 'offers.brand.deleteConfirm',
  deleted: 'offers.brand.deleted',
  deletedOne: 'offers.brand.deletedOne',
  count: 'offers.brand.count',
  countOne: 'offers.brand.countOne',
}

export function OfferBrandField({ projectId, token, value, known = [], onChange, onRemovedEverywhere }: {
  projectId: string
  token: string
  value: string
  /** Además de la lista del proyecto (`useOfferLabels`), que es la que manda. */
  known?: KnownOption[]
  onChange: (brand: string) => void
  /** Ya se quitó de todas las ofertas guardadas: la pantalla la quita también de lo suyo. */
  onRemovedEverywhere?: (name: string) => void
}) {
  const shared = useOfferLabels(projectId, token).brands
  return (
    <PickerField
      id="offer-brand"
      texts={BRAND_TEXTS}
      max={1}
      maxLength={80}
      value={value.trim() ? [value.trim()] : []}
      known={merged(shared, known)}
      onChange={(next) => onChange(next[0] ?? '')}
      removeEverywhere={(name) => removeOfferBrand(projectId, name, token).then((result) => ({ count: result.offers }))}
      onRemovedEverywhere={(name) => {
        if (value.trim().toLocaleLowerCase() === name.toLocaleLowerCase()) onChange('')
        onRemovedEverywhere?.(name)
      }}
    />
  )
}

const TAG_TEXTS = {
  label: 'offers.tags.title',
  placeholder: 'offers.tags.pickPlaceholder',
  create: 'offers.brand.create',
  empty: 'offers.tags.empty',
  allChosen: 'offers.tags.allChosen',
  limit: 'offers.tags.limit',
  remove: 'offers.tags.removeOne',
  deleteAria: 'offers.tags.deleteAria',
  deleteTitle: 'offers.tags.deleteTitle',
  deleteBody: 'offers.tags.deleteBody',
  deleteBodyOne: 'offers.tags.deleteBodyOne',
  deleteConfirm: 'offers.tags.deleteConfirm',
  deleted: 'offers.tags.deleted',
  deletedOne: 'offers.tags.deletedOne',
  count: 'offers.brand.count',
  countOne: 'offers.brand.countOne',
}

/** Las etiquetas de una oferta: varias, hasta 30. */
export function OfferTagsField({ projectId, token, value, known = [], max = 30, onChange, onRemovedEverywhere }: {
  projectId: string
  token: string
  value: string[]
  known?: KnownOption[]
  /** 1 en la condición de una colección: una etiqueta por condición. */
  max?: number
  onChange: (tags: string[]) => void
  onRemovedEverywhere?: (name: string) => void
}) {
  const shared = useOfferLabels(projectId, token).tags
  return (
    <PickerField
      id="offer-tags"
      texts={TAG_TEXTS}
      max={max}
      maxLength={40}
      value={value}
      known={merged(shared, known)}
      onChange={onChange}
      removeEverywhere={(name) => removeOfferTag(projectId, name, token).then((result) => ({ count: result.offers }))}
      onRemovedEverywhere={(name) => {
        const lower = name.toLocaleLowerCase()
        if (value.some((tag) => tag.toLocaleLowerCase() === lower)) onChange(value.filter((tag) => tag.toLocaleLowerCase() !== lower))
        onRemovedEverywhere?.(name)
      }}
    />
  )
}
